import { useState, useCallback } from 'react';

/**
 * Modal state interface
 */
export interface ModalState {
  activeModalId: string | null;
  modalData?: Record<string, any>;
}

/**
 * Modal manager interface
 */
export interface ModalManager {
  activeModalId: string | null;
  modalData?: Record<string, any>;
  openModal: (modalId: string, data?: Record<string, any>) => void;
  closeModal: () => void;
  isModalOpen: (modalId: string) => boolean;
}

/**
 * Custom hook for managing modal state
 * 
 * Provides centralized modal state management for the UnifiedTopBar.
 * Handles opening/closing modals and tracks which modal is currently active.
 * 
 * @returns Modal manager interface with state and control methods
 * 
 * @example
 * const { activeModalId, openModal, closeModal, isModalOpen } = useModalManager();
 * 
 * // Open a modal
 * openModal('export', { reportType: 'matters' });
 * 
 * // Close current modal
 * closeModal();
 * 
 * // Check if specific modal is open
 * if (isModalOpen('export')) { ... }
 */
export const useModalManager = (): ModalManager => {
  const [modalState, setModalState] = useState<ModalState>({
    activeModalId: null,
    modalData: undefined,
  });

  /**
   * Opens a modal by ID with optional data
   * 
   * @param modalId - ID of the modal to open
   * @param data - Optional data to pass to the modal
   */
  const openModal = useCallback((modalId: string, data?: Record<string, any>) => {
    setModalState({
      activeModalId: modalId,
      modalData: data,
    });
  }, []);

  /**
   * Closes the currently active modal
   */
  const closeModal = useCallback(() => {
    setModalState({
      activeModalId: null,
      modalData: undefined,
    });
  }, []);

  /**
   * Checks if a specific modal is currently open
   * 
   * @param modalId - ID of the modal to check
   * @returns true if the modal is open, false otherwise
   */
  const isModalOpen = useCallback(
    (modalId: string): boolean => {
      return modalState.activeModalId === modalId;
    },
    [modalState.activeModalId]
  );

  return {
    activeModalId: modalState.activeModalId,
    modalData: modalState.modalData,
    openModal,
    closeModal,
    isModalOpen,
  };
};

export default useModalManager;

